import { range } from 'lodash';
import moment, { Moment } from 'moment';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { format } from 'src/lib/format';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

import { CHAR_WIDTH, useHiddenTickIndexes } from './Axis';
import {
  HistogramBucket,
  getBucketAvg,
  getBucketFromPercentile,
  getBucketIndex,
} from './histogram';

const Y_AXIS_TICKET_INTERVAL = 30;

/**
 * Formats a given value according to metric notation.
 * E.g., 1000 would be converted to "1k", and so on.
 *
 * @param {number} value - The value to format.
 * @param {number} maxValue - The maximum value in the scale.
 * @returns {string} - The formatted string.
 */
export const defaultFormatValue = (value: number, maxValue: number) => {
  return format.metric(
    value,
    // If maxValue is >= 1, use a '0[.]0a' format string.
    // Otherwise, dynamically generate format based on maxValue decimal places.
    maxValue >= 1
      ? '0[.]0a'
      : `0[.]${maxValue.toString().match(/0+\.(0*)/)?.[1] ?? ''}00a`,
  );
};

/**
 * Custom React hook to calculate Y-axis tick labels and positions for a chart.
 *
 * @param {Object} options - An object containing various properties to customize the Y-axis.
 * @param {number} options.height - Height of the chart.
 * @param {number} options.maxValue - The maximum value of data points.
 * @param {Dispatch<SetStateAction<number>>} options.setMaxYLabelLength - State setter function to update maximum Y-axis label length.
 * @param {(value: number, maxValue: number): string} [options.formatValue=defaultFormatValue] - Optional function to format the Y-axis labels.
 * @returns {Array} - An array of tick values and their labels.
 */
export function useYAxisTicks({
  height,
  maxValue,
  setMaxYLabelLength,
  formatValue = defaultFormatValue,
}: {
  height: number;
  maxValue: number;
  setMaxYLabelLength: Dispatch<SetStateAction<number>>;
  formatValue?: (value: number, maxValue: number) => string;
}) {
  // Cache the calculated tick values and labels
  const yAxisTicks = useMemo(() => {
    // Calculate the number of ticks to be shown on the Y-axis
    // Minimum is 2 if maxValue is not 0, otherwise 0
    const numTicks = Math.max(
      Math.floor(height / Y_AXIS_TICKET_INTERVAL),
      maxValue === 0 ? 0 : 2,
    );

    // Generate the tick values and their corresponding labels
    const values = range(1, numTicks + 1).map((i) => {
      const value = (maxValue / numTicks) * i;
      return { value, labels: [formatValue(value, maxValue)] };
    });

    // Find the length of the longest label
    const maxLabelLength = Math.max(
      0,
      ...values.flatMap(({ labels }) => labels.map((label) => label.length)),
    );
    return { maxLabelLength, values };
  }, [formatValue, height, maxValue]);

  // Set the maximum Y-axis label length whenever it changes
  useEffect(() => {
    setMaxYLabelLength(yAxisTicks.maxLabelLength);
  }, [setMaxYLabelLength, yAxisTicks.maxLabelLength]);
  return yAxisTicks.values;
}

const X_TICK_LABEL_WIDTH = 58;
/**
 * Hook to calculate the ticks for the X axis when the axis represents time.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {d3.ScaleTime<number, number>} params.scale - The d3 scale object for time.
 * @param {number} params.width - The width of the axis in pixels.
 * @param {(Timestamp['output'] | Date | null)} params.cursorTimestamp - The current cursor timestamp, if any.
 * @param {(number | undefined)} params.tickIntervalMinutes - The interval between ticks in minutes.
 * @param {(Moment | undefined)} params.firstTickValue - The value of the first tick if it needs to be customized.
 * @param {(Timestamp['output'] | Date | undefined)} params.firstDate - The first date in the time series data.
 * @param {Dispatch<SetStateAction<{ labels: string[]; offset: number }>>} params.setFirstTick - Function to set the first tick.
 *
 * @returns {Object} - An object containing information about the ticks.
 * @returns {Object[]} ticks - The calculated tick objects.
 * @returns {Object | undefined} cursorTick - Tick object for the cursor position.
 * @returns {number[]} hiddenTickIndexs - Indexes of ticks that should be hidden.
 */
export function useXTimeAxisTicks({
  scale,
  width,
  cursorTimestamp,
  firstTickValue,
  tickIntervalMinutes,
  firstDate,
  setFirstTick,
}: {
  scale: d3.ScaleTime<number, number>;
  width: number;
  cursorTimestamp: Timestamp['output'] | Date | null;
  tickIntervalMinutes?: number;
  firstTickValue?: Moment;
  firstDate: undefined | Timestamp['output'] | Date;
  setFirstTick: Dispatch<SetStateAction<{ labels: string[]; offset: number }>>;
}) {
  // Calculate initial parameters for the axis scale and ticks.
  const firstTickDate = useMemo(
    () => firstTickValue ?? (firstDate && moment(firstDate)),
    [firstDate, firstTickValue],
  );
  const { scaleMinutes, domainStart, daysSpanned } = useMemo(() => {
    const [start, end] = scale.domain();
    return {
      domainStart: start,
      scaleMinutes: Math.max(
        1,
        moment
          .duration(moment(end).diff(moment(start)), 'milliseconds')
          .asMinutes(),
      ),
      daysSpanned: Math.abs(
        moment
          .duration(
            moment(start).startOf('day').diff(moment(end).startOf('day')),
          )
          .asDays(),
      ),
    };
  }, [scale]);

  // Generate an array of tick values.
  const values = useMemo(() => {
    const intervalMinutes =
      tickIntervalMinutes ??
      Math.ceil(scaleMinutes / (width / X_TICK_LABEL_WIDTH));
    const firstTickOffsetMinutes = firstTickDate
      ? moment
          .duration(firstTickDate.diff(domainStart), 'milliseconds')
          .asMinutes()
      : 0;
    const numTicks = Math.floor(
      (scaleMinutes - firstTickOffsetMinutes) / intervalMinutes,
    );
    const firstTickOffest = (scaleMinutes - numTicks * intervalMinutes) / 2;

    return Array(numTicks + 1)
      .fill(null)
      .map((_d, i) =>
        moment(firstTickDate ?? domainStart)
          .add(
            (firstTickDate ? 0 : firstTickOffest) + i * intervalMinutes,
            'minutes',
          )
          .toDate(),
      ) as MinLengthArray<1, Date>;
  }, [tickIntervalMinutes, scaleMinutes, width, firstTickDate, domainStart]);

  const labelInterval = Math.ceil(values.length / (width / X_TICK_LABEL_WIDTH));
  const formatTick = useCallback(
    (date: Timestamp['output'] | Date, index: number) => {
      const value = moment(date);
      const isCursorTick = cursorTimestamp && value.isSame(cursorTimestamp);

      let tickLabel = '';
      if (isCursorTick || index % labelInterval === 0) {
        const previousValue = values[index - labelInterval];
        if (
          daysSpanned >= values.length ||
          (!isCursorTick &&
            daysSpanned >= 1 &&
            (!previousValue ||
              !moment(previousValue)
                .startOf('day')
                .isSame(moment(value).startOf('day'))))
        ) {
          tickLabel = value.format('MMM Do');
        } else {
          tickLabel = value.format('h:mma');
        }
      }

      const valueDate = value.toDate();
      const tickWidth = CHAR_WIDTH * tickLabel.length;
      const tickPosition = scale(valueDate);

      return {
        value: valueDate,
        labels: tickLabel ? [tickLabel] : [],
        labelStart: tickPosition - tickWidth / 2,
        labelEnd: tickPosition + tickWidth / 2,
      };
    },
    [cursorTimestamp, daysSpanned, labelInterval, scale, values],
  );

  // Generate tick objects including labels and positions.
  const ticks = useMemo(() => values.map(formatTick), [formatTick, values]);

  // Generate the cursor tick object if cursorTimestamp is provided.
  const cursorTick = useMemo(() => {
    return cursorTimestamp
      ? formatTick(cursorTimestamp, ticks.length)
      : undefined;
  }, [cursorTimestamp, formatTick, ticks.length]);

  // Calculate which ticks should be hidden to avoid overlap or clutter.
  const hiddenTickIndexs = useHiddenTickIndexes({
    ticks,
    scale,
    cursorTick,
    side: 'bottom',
  });

  // Set the first tick's value and position if firstDate is provided.
  useEffect(() => {
    if (firstDate) {
      setFirstTick({
        labels: formatTick(firstDate, 0).labels,
        offset: scale(moment(firstDate)),
      });
    }
  }, [firstDate, formatTick, scale, setFirstTick]);
  return { ticks, cursorTick, hiddenTickIndexs };
}

export function usePercentileTicks(data: HistogramBucket[]) {
  return useMemo(() => {
    if (!data.length) return [];
    const p = (x: number) =>
      getBucketFromPercentile(data, x) ?? { index: 0, count: 0 };
    const max = p(1);
    const min = p(0);
    return [
      // ordered by priority: highest --> lowest
      { label: 'p50', ...p(0.5) },
      { label: 'max', ...max },
      { label: 'min', ...min },
      { label: 'p75', ...p(0.75) },
      { label: 'p25', ...p(0.25) },
      { label: 'p90', ...p(0.9) },
      { label: 'p99', ...p(0.99) },
      { label: 'p999', ...p(0.999) },

      // standard tick values, added at the lowest priority
      ...[1, 10, 100, 1000, 10000, 100000]
        .filter(
          (latency) =>
            (!max || latency < getBucketAvg(max.index)) &&
            (!min || latency > getBucketAvg(min.index)),
        )
        .map((latency) => ({
          label: '',
          index: getBucketIndex(latency),
        })),
    ].map((tick) => ({
      value: tick.index,
      labels: [
        `${format.ms(getBucketAvg(tick.index), 1, false)}`,
        ...(tick.label ? [tick.label] : []),
      ],
    }));
  }, [data]);
}
